"use client";

import { Button } from "@universe/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@universe/ui/components/dropdown-menu";
import {
  AlertTriangle,
  ChevronDown,
  Clock,
  Eye,
  LogOut,
  RefreshCw,
  ShieldOff,
  XCircle,
} from "lucide-react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useRef, useState } from "react";

import { LogoBackground } from "@/components/LogoBackground";
import { AccountSwitchList } from "@/components/account-switch-list";
import AccountTypeIcon from "@/components/account-type-icon";
import CompanyLogo from "@/components/company-logo";
import { NoTranslate } from "@/components/no-translate";
import { SupportContact } from "@/components/support-contact";
import { Route } from "@/enums/route";
import { useLogout } from "@/hooks/useLogout";
import { useSupportContact } from "@/hooks/useSupportContact";
import { trackEvent } from "@/lib/analytics";
import { reportSilentFailure } from "@/lib/reportSilentFailure";
import { useCustomerAccount } from "@/providers";
import { removeLoader } from "@/splashScreen";
import { getInitials } from "@/utils";
import { resolveAccountAccess } from "@/utils/accountStateAccess";
import { AccountState, CustomerAccountUser } from "@generated/types";

import { SuspendedRouteGuard } from "./SuspendedRouteGuard";

const STATE_CONFIG = {
  [AccountState.Opening]: {
    icon: Clock,
    iconColor: "text-amber-500",
    bgColor: "bg-amber-50",
    translationKey: "opening" as const,
    showRefresh: true,
  },
  // Compliance passed, operations has not provisioned the account yet. The
  // state resolves on its own, so refresh is offered like OPENING.
  [AccountState.Accepted]: {
    icon: Clock,
    iconColor: "text-green-600",
    bgColor: "bg-green-50",
    translationKey: "accepted" as const,
    showRefresh: true,
  },
  [AccountState.Suspended]: {
    icon: ShieldOff,
    iconColor: "text-muted-foreground",
    bgColor: "bg-gray-50",
    translationKey: "suspended" as const,
    showRefresh: false,
  },
  [AccountState.Closed]: {
    icon: XCircle,
    iconColor: "text-red-500",
    bgColor: "bg-red-50",
    translationKey: "closed" as const,
    showRefresh: false,
  },
  // Terminal: compliance rejected the account at verdict time.
  [AccountState.Rejected]: {
    icon: XCircle,
    iconColor: "text-red-500",
    bgColor: "bg-red-50",
    translationKey: "rejected" as const,
    showRefresh: false,
  },
} as const;

// Fallback for unknown non-OPEN states (e.g. if backend adds a new state)
const UNKNOWN_STATE_CONFIG = {
  icon: AlertTriangle,
  iconColor: "text-amber-500",
  bgColor: "bg-amber-50",
  translationKey: "unknown" as const,
  showRefresh: true,
};

function AccountStateBlockingPage({
  accountState,
  onContinue,
}: {
  accountState: AccountState;
  /**
   * When set (SUSPENDED accounts), the page is a dismissible warning rather
   * than a hard block: a "continue in view-only mode" action is rendered.
   */
  onContinue?: () => void;
}) {
  const t = useTranslations("accountStateGate");
  const tNav = useTranslations("nav");
  const logout = useLogout();
  const router = useRouter();
  const {
    customerAccount,
    customerAccounts,
    customerAccountId,
    changeCustomerAccount,
    setIsSwitchingAccount,
    userProfile,
  } = useCustomerAccount();
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    // Dismiss the HTML splash overlay so this page is visible.
    // SplashScreen normally calls removeLoader(), but it never mounts
    // when AccountStateGate blocks — so we must call it here.
    removeLoader();
    headingRef.current?.focus();
  }, []);

  const knownConfig = STATE_CONFIG[accountState as keyof typeof STATE_CONFIG];
  const config = knownConfig ?? UNKNOWN_STATE_CONFIG;

  // Opening/Suspended/Closed blocking is intended business UX, but the
  // unknown-state fallback means the frontend is full-screen-blocking a
  // customer over a state it doesn't recognise (backend contract drift) —
  // /self returned 200, so nothing else reports it. Enum values are a
  // fixed vocabulary, PII-safe.
  const isUnknownState = !knownConfig;
  useEffect(() => {
    if (isUnknownState) {
      reportSilentFailure({
        message: "[Account] Unknown accountState is blocking the app",
        level: "warning",
        fingerprint: "account-state-unknown",
        tags: { accountState: String(accountState) },
      });
    }
  }, [isUnknownState, accountState]);

  const Icon = config.icon;

  const { supportEmail, supportPhone } = useSupportContact();

  const handleLogout = () => {
    logout();
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  // Switching away is the escape hatch from a non-OPEN account: users with a
  // second (OPEN) account must not be trapped on this page. No app content is
  // mounted here, so switch immediately — the gate re-evaluates from context
  // (OPEN → app boots; another non-OPEN → that account's page shows).
  const handleSelectAccount = (account: CustomerAccountUser) => {
    setIsSwitchingAccount(true);
    changeCustomerAccount(
      account.id ?? null,
      account.customerAccount?.name || ""
    );
    router.push(Route.Dashboard);
  };

  return (
    <div className="flex min-h-screen flex-col bg-brand-primary/5">
      {/* Top bar with user menu dropdown (mirrors breadcrumb-header pattern) */}
      <header className="flex h-16 shrink-0 items-center justify-end px-4 py-4 border-b border-line">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              aria-label={tNav("userMenu")}
              className="group/brand-hover relative flex items-center gap-2 px-2 py-1 md:py-1.5 md:gap-2.5 rounded-full backdrop-blur-sm border border-white/30 transition-all duration-200 shadow-md hover:cursor-pointer text-sm"
              style={{
                background:
                  "linear-gradient(to bottom right, oklch(from var(--header-button-color, var(--brand-primary)) l c h / 0.9), oklch(from var(--header-button-color, var(--brand-primary)) l c h / 0.8))",
              }}
            >
              <div className="brand-hover-overlay rounded-full z-[1]" />
              {/* Mobile: initials */}
              <div
                className="relative z-[2] flex md:hidden items-center justify-center w-7 h-7 rounded-full font-semibold text-sm shadow-md"
                style={{
                  backgroundColor:
                    "var(--header-button-icon-background, var(--brand-primary))",
                  color: "var(--menu-font-color)",
                  textShadow:
                    "-0.5px 0.5px 1px color-mix(in oklch, var(--menu-font-shadow) 20%, transparent), 0 0.5px 2px color-mix(in oklch, var(--menu-font-shadow) 40%, transparent), 1px 1px 0px color-mix(in oklch, var(--menu-font-shadow) 40%, transparent)",
                }}
              >
                {getInitials(customerAccount?.customerAccount?.name)}
              </div>
              {/* Desktop: account type icon */}
              <div
                className="relative z-[2] hidden md:flex items-center justify-center w-7 h-7 rounded-full shadow-md"
                style={{
                  backgroundColor:
                    "var(--header-button-icon-background, var(--brand-primary))",
                }}
              >
                <AccountTypeIcon
                  type={customerAccount?.customerAccount?.accountType || ""}
                  label=""
                  showLabel={false}
                  size={16}
                  iconClassName=""
                  iconStyle={{
                    color: "var(--menu-font-color)",
                    filter:
                      "drop-shadow(0 0px 1px color-mix(in oklch, var(--menu-font-shadow) 40%, transparent)) drop-shadow(0 0px 1px color-mix(in oklch, var(--menu-font-shadow) 60%, transparent))",
                  }}
                />
              </div>
              <span
                data-testid="customer-account-username"
                className="relative z-[2] hidden md:block text-base font-medium lg:max-w-full truncate"
                style={{
                  color: "var(--menu-font-color)",
                  textShadow:
                    "-0.5px 0.5px 1px color-mix(in oklch, var(--menu-font-shadow) 20%, transparent), 0 0.5px 2px color-mix(in oklch, var(--menu-font-shadow) 40%, transparent), 1px 1px 0px color-mix(in oklch, var(--menu-font-shadow) 40%, transparent)",
                }}
              >
                <NoTranslate>
                  {customerAccount?.customerAccount?.name}
                </NoTranslate>
              </span>
              <ChevronDown
                className="relative z-[2] h-4 w-4 md:h-5 md:w-5"
                style={{
                  color: "var(--menu-font-color)",
                  filter:
                    "drop-shadow(0 0px 1px color-mix(in oklch, var(--menu-font-shadow) 40%, transparent)) drop-shadow(0 0px 1px color-mix(in oklch, var(--menu-font-shadow) 60%, transparent))",
                }}
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-64 rounded-lg drop-shadow-md"
            side="bottom"
            align="end"
            sideOffset={8}
          >
            <div className="px-2 py-2">
              <div className="grid text-left text-sm leading-tight gap-0.5">
                <span className="text-xs text-muted-foreground font-medium">
                  {tNav("loggedInAs")}
                </span>
                <span
                  className="truncate font-bold text-gray-900 text-base"
                  data-testid="user-full-name"
                >
                  <NoTranslate>{userProfile?.fullName}</NoTranslate>
                </span>
              </div>
            </div>
            <DropdownMenuSeparator className="my-1" />
            {/* Account switching is the escape hatch for users whose OTHER
                account is usable — the blocking page must not trap them. */}
            <AccountSwitchList
              accounts={customerAccounts ?? []}
              activeAccountId={customerAccountId}
              onSelect={handleSelectAccount}
            />
            <DropdownMenuSeparator className="my-2" />
            <DropdownMenuItem
              className="hover:bg-brand-primary/10 hover:cursor-pointer font-medium"
              onClick={handleLogout}
            >
              <LogOut className="text-gray-700" />
              <span>{tNav("logout")}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Main content */}
      <main className="flex flex-1 flex-col items-center justify-start pt-8 md:pt-16 px-4">
        <div className="w-full max-w-lg">
          <div className="bg-white rounded-2xl shadow-md px-5 md:px-8 py-8 md:py-12 space-y-6">
            {/* Company logo */}
            <LogoBackground className="flex justify-center max-w-[260px] mx-auto !mb-0">
              <CompanyLogo variant="default" />
            </LogoBackground>

            {/* State icon */}
            <div className="flex justify-center">
              <div
                className={`flex h-16 w-16 items-center justify-center rounded-full ${config.bgColor}`}
              >
                <Icon className={`h-8 w-8 ${config.iconColor}`} />
              </div>
            </div>

            {/* Title & message */}
            <div className="text-center space-y-3">
              <h1
                ref={headingRef}
                tabIndex={-1}
                className="text-lg md:text-2xl font-bold text-gray-900 outline-none"
              >
                {t(`${config.translationKey}.title`)}
              </h1>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t(`${config.translationKey}.message`)}
              </p>
            </div>

            {/* Support contact */}
            <SupportContact
              className="border-t border-line-soft pt-4"
              heading={
                <p className="text-sm text-muted-foreground text-center">
                  {t(`${config.translationKey}.supportNote`)}
                </p>
              }
              emailAriaLabel={
                supportEmail
                  ? t("aria.emailSupport", { email: supportEmail })
                  : undefined
              }
              phoneAriaLabel={
                supportPhone
                  ? t("aria.callSupport", { phone: supportPhone })
                  : undefined
              }
            />

            {/* Continue action — only for SUSPENDED (view-only mode) */}
            {onContinue && (
              <div className="pt-2 space-y-3">
                <p className="text-sm text-muted-foreground leading-relaxed text-center">
                  {t("suspended.viewOnlyHint")}
                </p>
                <Button
                  onClick={onContinue}
                  className="w-full gap-2"
                  data-testid="suspended-continue-button"
                >
                  <Eye className="h-4 w-4" />
                  <span>{t("continueViewOnly")}</span>
                </Button>
              </div>
            )}

            {/* Refresh button — only for OPENING (processing may complete) */}
            {}
            {config.showRefresh && (
              <div className="pt-2">
                <Button
                  onClick={handleRefresh}
                  variant="outline"
                  className="w-full gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  <span>{t("refreshPage")}</span>
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

/**
 * SUSPENDED (view-only) branch. The warning acknowledgement is in-memory
 * state on purpose (not sessionStorage): the warning must re-appear on every
 * login, full reload, and every switch TO the suspended account — suspension
 * is a rare, high-severity state. AccountStateGate keys this component by
 * customerAccountId, so an account switch remounts it and resets the
 * acknowledgement automatically.
 */
function SuspendedViewOnlyGate({ children }: { children: React.ReactNode }) {
  const [acknowledged, setAcknowledged] = useState(false);

  const handleContinueViewOnly = useCallback(() => {
    trackEvent("Suspended View-Only Entered");
    setAcknowledged(true);
  }, []);

  if (!acknowledged) {
    return (
      <AccountStateBlockingPage
        accountState={AccountState.Suspended}
        onContinue={handleContinueViewOnly}
      />
    );
  }

  return <SuspendedRouteGuard>{children}</SuspendedRouteGuard>;
}

export function AccountStateGate({ children }: { children: React.ReactNode }) {
  const { customerAccount, customerAccountId } = useCustomerAccount();

  const accountState = customerAccount?.customerAccount?.accountState;
  const access = resolveAccountAccess(accountState);

  if (access === "blocked") {
    return (
      <AccountStateBlockingPage accountState={accountState as AccountState} />
    );
  }

  if (access === "viewOnly") {
    return (
      <SuspendedViewOnlyGate key={customerAccountId ?? "none"}>
        {children}
      </SuspendedViewOnlyGate>
    );
  }

  return <>{children}</>;
}
